import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiTrash2, FiImage, FiPlus } = FiIcons;

function MultiImageSection({ 
  title, 
  description, 
  icon, 
  colorClass, 
  images, 
  onAddImage, 
  onDeleteImage, 
  maxImages = 5 
}) {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && images.length < maxImages) {
      setUploading(true);
      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const base64String = event.target.result;
          await onAddImage(base64String, file.name);
          setUploading(false);
        };
        reader.onerror = () => {
          console.error('Error reading file');
          setUploading(false);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading image:', error);
        setUploading(false);
      }
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await onDeleteImage(imageId);
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const getGradientClass = (color) => {
    const gradients = {
      purple: 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600',
      rose: 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600',
      red: 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600',
      gray: 'bg-gradient-to-r from-gray-500 to-slate-500 hover:from-gray-600 hover:to-slate-600',
      lime: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
    };
    return gradients[color] || gradients.purple;
  };

  const getHeaderGradientClass = (color) => {
    const gradients = {
      purple: 'bg-gradient-to-r from-purple-600 to-indigo-600',
      rose: 'bg-gradient-to-r from-pink-500 to-rose-500',
      red: 'bg-gradient-to-r from-red-500 to-pink-500',
      gray: 'bg-gradient-to-r from-gray-500 to-slate-500',
      lime: 'bg-gradient-to-r from-green-500 to-emerald-500',
    };
    return gradients[color] || gradients.purple;
  };

  const getTextGradientClass = (color) => {
    const gradients = {
      purple: 'bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent',
      rose: 'bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent',
      red: 'bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent',
      gray: 'bg-gradient-to-r from-gray-500 to-slate-500 bg-clip-text text-transparent',
      lime: 'bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent',
    };
    return gradients[color] || gradients.purple;
  };

  return (
    <div className="mt-8 mb-8 p-8 bg-white rounded-3xl shadow-xl border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className={`${getHeaderGradientClass(colorClass)} p-3 rounded-2xl mr-4 shadow-lg`}>
            <SafeIcon icon={icon} className="text-white text-4xl" />
          </div>
          <div>
            <h2 className={`text-3xl font-bold ${getTextGradientClass(colorClass)}`}>
              {title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {images.length}/{maxImages} immagini caricate
            </p>
          </div>
        </div>

        {/* Upload Button */}
        {images.length < maxImages && (
          <label className={`p-4 ${getGradientClass(colorClass)} text-white rounded-2xl shadow-lg cursor-pointer transform hover:scale-105 transition-all duration-200 flex items-center gap-2 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="font-semibold">Caricamento...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiPlus} className="text-xl" />
                <span className="font-semibold">Aggiungi Immagine</span>
              </>
            )}
            <input 
              type="file" 
              onChange={handleFileChange} 
              className="hidden" 
              accept="image/*"
              disabled={uploading || images.length >= maxImages}
            />
          </label>
        )}
      </div>

      <p className="text-gray-600 mb-6 text-lg">
        {description}
      </p>

      {/* Images Grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <div className="relative p-3 bg-gradient-to-r from-gray-50 to-white rounded-3xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
                <img 
                  src={image.image_url} 
                  alt={image.filename || 'Immagine caricata'}
                  className="w-full h-48 rounded-2xl shadow-lg object-cover"
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/600x400/E1306C/FFFFFF?text=Immagine+non+trovata';
                  }}
                />
                
                {/* Delete button */}
                <button
                  onClick={() => handleDeleteImage(image.id)}
                  className="absolute top-1 right-1 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transform hover:scale-110 transition-all duration-200 opacity-0 group-hover:opacity-100"
                  title="Elimina immagine"
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                </button>

                {/* Image info */}
                {image.filename && (
                  <p className="mt-2 text-xs text-gray-500 truncate px-2">
                    {image.filename}
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* Add more placeholder */}
          {images.length < maxImages && (
            <div className="relative group">
              <label className={`block p-8 border-2 border-dashed border-gray-300 rounded-3xl text-center cursor-pointer hover:border-gray-400 transition-all duration-200 ${getGradientClass(colorClass)} hover:opacity-90`}>
                <SafeIcon icon={FiPlus} className="text-4xl text-white mx-auto mb-4" />
                <p className="text-white font-semibold">
                  Aggiungi un'altra immagine
                </p>
                <p className="text-white text-sm opacity-80 mt-2">
                  {maxImages - images.length} rimanenti
                </p>
                <input 
                  type="file" 
                  onChange={handleFileChange} 
                  className="hidden" 
                  accept="image/*"
                  disabled={uploading}
                />
              </label>
            </div>
          )}
        </div>
      ) : (
        <div className="p-12 border-2 border-dashed border-gray-300 rounded-3xl text-center">
          <SafeIcon icon={FiImage} className="text-6xl text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-xl mb-4">
            Nessuna immagine caricata
          </p>
          <label className={`inline-flex items-center gap-3 p-4 ${getGradientClass(colorClass)} text-white font-semibold rounded-2xl shadow-lg cursor-pointer transform hover:scale-105 transition-all duration-200`}>
            <SafeIcon icon={FiUpload} className="text-xl" />
            Carica la prima immagine
            <input 
              type="file" 
              onChange={handleFileChange} 
              className="hidden" 
              accept="image/*"
              disabled={uploading}
            />
          </label>
        </div>
      )}

      {/* Limit reached message */}
      {images.length >= maxImages && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-2xl text-center">
          <p className="text-blue-700 font-semibold">
            ✨ Hai raggiunto il limite massimo di {maxImages} immagini per questa sezione
          </p>
        </div>
      )}
    </div>
  );
}

export default MultiImageSection;